import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { abs } from "@/lib/site";
import { SymbolExport } from "@/components/SymbolExport";
import { SymbolFormats } from "@/components/SymbolFormats";
import { SymbolHowToType } from "@/components/SymbolHowToType";
import { SymbolVariants } from "@/components/SymbolVariants";
import { CollectButton } from "@/components/CollectButton";
import { CopyAsMenu } from "@/components/CopyAsMenu";
import { FavoriteButton } from "@/components/FavoriteButton";
import { copyText } from "@/lib/copy";
import { recordView } from "@/lib/library";
import { categories, getCategory, symbolSlug } from "@/data/symbols";
import { codepointLabel, getSymbolName } from "@/data/symbol-names";
// NOTE: the heavy data modules (symbol-resolve, symbol-index, symbol-prose,
// emoji-variants — together ~1.6 MB) are dynamically imported INSIDE the loader
// below. routeTree.gen.ts statically imports every route module, so a static
// import here would pull that data into the global entry bundle that every page
// loads. Dynamic import keeps it server-only on SSR (data is serialized into the
// page) and lazy on client navigation.
import { htmlHexEntity, htmlNamedEntity } from "@/lib/symbol-formats";

/**
 * H1 heading for a symbol page — always ends with the word "Symbol" for SEO
 * (e.g. "Black Heart Suit Symbol"), unless the UCD name already contains it
 * (e.g. "Recycling Symbol", "Symbol For Null", APL functional symbols).
 */
function symbolHeading(name: string): string {
  return /\bsymbols?\b/i.test(name) ? name : `${name} Symbol`;
}

export const Route = createFileRoute("/symbol/$slug")({
  loader: async ({ params }) => {
    const [
      { findSymbolBySlug, getLookalikes, getSiblingNav },
      { getRecordBySlug },
      { getProse },
      { getPresentationVariants, getSkinToneVariants, getZwjMembers },
    ] = await Promise.all([
      import("@/data/symbol-resolve"),
      import("@/data/symbol-index.generated"),
      import("@/data/symbol-prose"),
      import("@/lib/emoji-variants"),
    ]);

    const entry = findSymbolBySlug(params.slug);
    if (!entry) throw notFound();
    const category = getCategory(entry.categorySlug)!;
    const name = getSymbolName(entry.symbol);
    const codepoints = codepointLabel(entry.symbol);

    // Visual lookalikes (UTS#39 confusables) — the headline related set.
    const lookalikes = getLookalikes(entry.symbol).map((s) => ({
      symbol: s.symbol,
      slug: s.slug,
      name: getSymbolName(s.symbol),
    }));

    // Siblings inside the same curated category.
    const siblings = category.symbols
      .filter((s) => s !== entry.symbol)
      .slice(0, 12)
      .map((s) => ({ symbol: s, slug: symbolSlug(s), name: getSymbolName(s) }));

    // Cross-category interlinking — picks from other collections with one sample.
    const otherCategories = categories
      .filter((c) => c.slug !== category.slug)
      .slice(0, 6)
      .map((c) => {
        const sample = c.symbols[0];
        return {
          slug: c.slug,
          name: c.name,
          tagline: c.tagline,
          emoji: c.emoji,
          sampleSymbol: sample,
          sampleSlug: symbolSlug(sample),
          sampleName: getSymbolName(sample),
        };
      });

    const nav = getSiblingNav(entry);
    const prev = nav.prev
      ? { slug: nav.prev.slug, symbol: nav.prev.symbol, name: getSymbolName(nav.prev.symbol) }
      : undefined;
    const next = nav.next
      ? { slug: nav.next.slug, symbol: nav.next.symbol, name: getSymbolName(nav.next.symbol) }
      : undefined;
    const position = nav.index >= 0 ? nav.index + 1 : 1;
    const total = nav.total;

    const prose = getProse(entry.slug);

    // Phase 10 — emoji modifier / VS / ZWJ variants. Only computed for
    // codepoints that actually carry the corresponding UCD property.
    const record = getRecordBySlug(entry.slug)!;
    const skinTones = getSkinToneVariants(record);
    const presentation = getPresentationVariants(record);
    const zwjMembers = getZwjMembers(record, getSymbolName);
    // Multicolor emoji render in their own colors and ignore an export fill, so
    // the export color picker/preview are only useful for monochrome glyphs.
    const colorable = record.defaultPresentation !== "emoji";

    return { entry, category, name, codepoints, lookalikes, siblings, otherCategories, prev, next, position, total, prose, skinTones, presentation, zwjMembers, colorable };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const { entry, category, name, codepoints } = loaderData;
    const heading = symbolHeading(name); // "…Symbol" — aligns title/OG with the H1
    const title = `${entry.symbol} ${heading} — copy and paste`;
    const description = `Copy the ${name} symbol (${entry.symbol}) with one click. ${codepoints}. Part of the ${category.name} collection — works on web, mobile and desktop.`;
    // Pre-generated PNG card (scripts/build-og-images.mjs). PNG, not the SVG
    // this used to point at: no social crawler accepts SVG for og:image, so
    // those shares rendered as blank cards.
    const ogImage = abs(`/og/${params.slug}.png`);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: `${entry.symbol} ${heading}` },
        { property: "og:description", content: description },
        { property: "og:url", content: abs(`/symbol/${params.slug}`) },
        { property: "og:type", content: "article" },
        { property: "og:image", content: ogImage },
        { property: "og:image:type", content: "image/png" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: `${name} (${entry.symbol})` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${entry.symbol} ${heading}` },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: abs(`/symbol/${params.slug}`) }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DefinedTerm",
            name,
            alternateName: entry.symbol,
            description,
            url: `/symbol/${params.slug}`,
            image: ogImage,
            identifier: codepoints,
            inDefinedTermSet: {
              "@type": "DefinedTermSet",
              name: category.name,
              url: `/categories/${category.slug}`,
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: abs("/") },
              { "@type": "ListItem", position: 2, name: "Categories", item: abs("/categories") },
              { "@type": "ListItem", position: 3, name: category.name, item: abs(`/categories/${category.slug}`) },
              { "@type": "ListItem", position: 4, name: heading, item: abs(`/symbol/${params.slug}`) },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: `Related ${loaderData.category.name}`,
            itemListOrder: "https://schema.org/ItemListOrderAscending",
            numberOfItems: loaderData.siblings.length,
            itemListElement: loaderData.siblings.map((s, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: s.name,
              url: `/symbol/${s.slug}`,
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": `/symbol/${params.slug}`,
            url: `/symbol/${params.slug}`,
            name: `${name} (${entry.symbol})`,
            description,
            isPartOf: { "@type": "WebSite", name: "CoolSymbols.cool", url: "/" },
            primaryImageOfPage: {
              "@type": "ImageObject",
              url: ogImage,
              width: 1200,
              height: 630,
              caption: `${name} (${entry.symbol})`,
            },
            mainEntity: {
              "@type": "CreativeWork",
              "@id": `/symbol/${params.slug}#symbol`,
              name,
              alternateName: [entry.symbol, codepoints],
              text: entry.symbol,
              identifier: codepoints,
              inLanguage: "und",
              encodingFormat: "text/plain",
              keywords: [
                name.toLowerCase(),
                `${name.toLowerCase()} symbol`,
                `copy ${name.toLowerCase()}`,
                category.name.toLowerCase(),
                `unicode ${codepoints.toLowerCase()}`,
              ],
              genre: category.name,
              about: {
                "@type": "DefinedTermSet",
                name: category.name,
                url: `/categories/${category.slug}`,
              },
              license: "https://creativecommons.org/publicdomain/zero/1.0/",
              isAccessibleForFree: true,
              encoding: [
                {
                  "@type": "MediaObject",
                  encodingFormat: "image/svg+xml",
                  contentUrl: `/symbol/${params.slug}#export-svg`,
                  name: `${name} as SVG`,
                },
                {
                  "@type": "MediaObject",
                  encodingFormat: "image/png",
                  contentUrl: `/symbol/${params.slug}#export-png`,
                  name: `${name} as PNG`,
                },
              ],
            },
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <div className="flex flex-1 items-center justify-center p-12 text-center">
        <div>
          <h1 className="font-display text-3xl font-bold">Symbol not found</h1>
          <Link to="/categories" className="mt-4 inline-block text-accent hover:underline">
            ← Browse all categories
          </Link>
        </div>
      </div>
      <SiteFooter />
    </div>
  ),
  component: SymbolPage,
});

function SymbolPage() {
  const { entry, category, name, codepoints, lookalikes, siblings, otherCategories, prev, next, position, total, prose, skinTones, presentation, zwjMembers, colorable } = Route.useLoaderData();
  const [copied, setCopied] = useState(false);

  // Phase 12 — record a "recently viewed" entry whenever the user lands on
  // a symbol page (and again on slug change for client-side navigations).
  useEffect(() => {
    recordView(entry.symbol);
  }, [entry.symbol]);

  const copy = useCallback(async () => {
    const ok = await copyText(entry.symbol, { source: "symbol-detail", symbol: entry.symbol });
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  }, [entry.symbol]);

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />

      <main className="flex-1">
        <section className="mx-auto w-full max-w-5xl px-4 pt-6 sm:px-6 sm:pt-14">
          <nav className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground sm:text-sm">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <span>/</span>
            <Link to="/categories" className="hover:text-foreground">Categories</Link>
            <span>/</span>
            <Link
              to="/categories/$slug"
              params={{ slug: category.slug }}
              className="hover:text-foreground"
            >
              {category.name}
            </Link>
            <span>/</span>
            <span className="min-w-0 break-words text-foreground">{symbolHeading(name)}</span>
          </nav>

          <div className="glass grid grid-cols-1 gap-6 rounded-3xl p-5 sm:grid-cols-[auto_1fr] sm:gap-8 sm:p-10">
            <button
              type="button"
              onClick={copy}
              aria-label={`Copy ${name}`}
              className="symbol-tile relative mx-auto flex h-36 w-36 items-center justify-center text-6xl sm:mx-0 sm:h-56 sm:w-56 sm:text-8xl"
            >
              <span aria-hidden="true">{entry.symbol}</span>
            </button>

            <div className="flex flex-col">
              <h1 className="font-display text-3xl font-bold sm:text-4xl">
                {symbolHeading(name)}
              </h1>
              <p className="mt-2 text-muted-foreground">
                A symbol from the {category.name} collection. Click the symbol or
                the button below to copy.
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={copy}
                  className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
                >
                  {copied ? "Copied!" : `Copy ${entry.symbol}`}
                </button>
                <CopyAsMenu symbol={entry.symbol} />
                <FavoriteButton symbol={entry.symbol} />
                <CollectButton symbol={entry.symbol} variant="chip" />
              </div>

              <dl className="mt-6 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                <div className="rounded-lg border border-border bg-surface/40 p-3">
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground">Symbol</dt>
                  <dd className="mt-1 font-mono text-base">{entry.symbol}</dd>
                </div>
                <div className="rounded-lg border border-border bg-surface/40 p-3">
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground">Unicode</dt>
                  <dd className="mt-1 font-mono text-base">{codepoints}</dd>
                </div>
                <div className="rounded-lg border border-border bg-surface/40 p-3">
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground">HTML entity</dt>
                  <dd className="mt-1 font-mono text-base">
                    {htmlNamedEntity(entry.symbol) ?? htmlHexEntity(entry.symbol)}
                  </dd>
                </div>
                <div className="rounded-lg border border-border bg-surface/40 p-3">
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground">Category</dt>
                  <dd className="mt-1">
                    <Link
                      to="/categories/$slug"
                      params={{ slug: category.slug }}
                      className="text-accent hover:underline"
                    >
                      {category.name}
                    </Link>
                  </dd>
                </div>
              </dl>

              <SymbolExport
                symbol={entry.symbol}
                slug={entry.slug}
                name={name}
                colorable={colorable}
              />
            </div>
          </div>
        </section>

        {/* Prev / Next sibling nav within the same category — crawl depth */}
        <section className="mx-auto w-full max-w-5xl px-4 pt-6 sm:px-6 sm:pt-8" aria-label="Symbol navigation">
          <nav className="glass grid grid-cols-2 gap-2 rounded-2xl p-2 sm:gap-3 sm:p-3">
            {prev ? (
              <Link
                to="/symbol/$slug"
                params={{ slug: prev.slug }}
                rel="prev"
                className="group flex min-w-0 items-center gap-2 rounded-xl px-2 py-2 transition-colors hover:bg-secondary sm:gap-3 sm:px-3"
                title={`Previous: ${prev.name}`}
              >
                <span className="text-muted-foreground group-hover:text-foreground">←</span>
                <span className="flex h-9 w-9 flex-none items-center justify-center rounded-md border border-border bg-surface/60 text-xl">
                  {prev.symbol}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">
                    Previous
                  </span>
                  <span className="block truncate text-xs font-medium sm:text-sm">
                    {prev.name}
                  </span>
                </span>
              </Link>
            ) : (
              <span />
            )}
            {next ? (
              <Link
                to="/symbol/$slug"
                params={{ slug: next.slug }}
                rel="next"
                className="group flex min-w-0 items-center justify-end gap-2 rounded-xl px-2 py-2 text-right transition-colors hover:bg-secondary sm:gap-3 sm:px-3"
                title={`Next: ${next.name}`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">
                    Next
                  </span>
                  <span className="block truncate text-xs font-medium sm:text-sm">
                    {next.name}
                  </span>
                </span>
                <span className="flex h-9 w-9 flex-none items-center justify-center rounded-md border border-border bg-surface/60 text-xl">
                  {next.symbol}
                </span>
                <span className="text-muted-foreground group-hover:text-foreground">→</span>
              </Link>
            ) : (
              <span />
            )}
          </nav>
          <p className="mt-2 text-center text-[11px] text-muted-foreground">
            {position} of {total} in the {category.name.toLowerCase()} collection
          </p>
        </section>

        {prose && prose.length > 0 ? (
          <section
            aria-labelledby="about-heading"
            className="mx-auto w-full max-w-5xl px-4 pt-10 sm:px-6 sm:pt-12"
          >
            <h2 id="about-heading" className="font-display text-xl font-semibold sm:text-2xl">
              About the {name.toLowerCase()} symbol
            </h2>
            <div className="prose prose-invert mt-4 max-w-none space-y-4 text-foreground/90">
              {prose.map((p: string, i: number) => (
                <p key={i} className="text-[15px] leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          </section>
        ) : null}

        <SymbolHowToType symbol={entry.symbol} name={name} />
        <SymbolFormats symbol={entry.symbol} />

        <SymbolVariants
          symbolName={name}
          skinTones={skinTones}
          presentation={presentation}
          zwjMembers={zwjMembers}
        />


        {/* Phase 5 — Lookalikes (UTS#39 confusables) headline cluster. */}
        {lookalikes.length > 0 ? (
          <section
            aria-labelledby="lookalikes-heading"
            className="mx-auto w-full max-w-5xl px-4 pt-10 sm:px-6 sm:pt-12"
          >
            <h2 id="lookalikes-heading" className="font-display text-xl font-semibold sm:text-2xl">
              Looks similar to {entry.symbol}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Visually confusable Unicode characters — distinct codepoints that
              can be mistaken for the {name.toLowerCase()}. Useful for
              disambiguation, security review, and finding the variant you
              actually want.
            </p>
            <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {lookalikes.map((s: { symbol: string; slug: string; name: string }) => (
                <li key={s.slug}>
                  <Link
                    to="/symbol/$slug"
                    params={{ slug: s.slug }}
                    className="glass flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors hover:border-primary/40"
                    title={`${s.name} — looks like ${entry.symbol}`}
                  >
                    <span className="flex h-9 w-9 flex-none items-center justify-center rounded-md border border-border bg-surface/60 text-xl">
                      {s.symbol}
                    </span>
                    <span className="min-w-0 flex-1 truncate">
                      <span className="font-medium">{s.name}</span>
                      <span className="block text-xs text-muted-foreground">
                        Looks like {entry.symbol} {name.toLowerCase()}
                      </span>
                    </span>
                    <span className="text-muted-foreground">→</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* Related symbols — rich anchor text for topical SEO */}
        <section className="mx-auto w-full max-w-5xl px-4 pt-10 sm:px-6 sm:pt-12">
          <h2 className="font-display text-xl font-semibold sm:text-2xl">
            Related {category.name.toLowerCase()}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Other {category.name.toLowerCase()} you can copy and paste — explore
            similar Unicode characters from the same family.
          </p>
          <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {siblings.map((s: { symbol: string; slug: string; name: string }) => (
              <li key={s.slug}>
                <Link
                  to="/symbol/$slug"
                  params={{ slug: s.slug }}
                  className="glass flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors hover:border-primary/40"
                  title={`${s.name} — copy ${s.symbol}`}
                >
                  <span className="flex h-9 w-9 flex-none items-center justify-center rounded-md border border-border bg-surface/60 text-xl">
                    {s.symbol}
                  </span>
                  <span className="min-w-0 flex-1 truncate">
                    <span className="font-medium">{s.name}</span>
                    <span className="block text-xs text-muted-foreground">
                      Copy the {s.name.toLowerCase()} symbol
                    </span>
                  </span>
                  <span className="text-muted-foreground">→</span>
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-5">
            <Link
              to="/categories/$slug"
              params={{ slug: category.slug }}
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
            >
              See all {category.name.toLowerCase()} →
            </Link>
          </div>
        </section>

        {/* Cross-category interlinking — strengthens topical graph site-wide */}
        <section className="mx-auto w-full max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
          <h2 className="font-display text-xl font-semibold sm:text-2xl">
            Explore related collections
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Browse other symbol families that pair well with{" "}
            {category.name.toLowerCase()}.
          </p>
          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {otherCategories.map((c: { slug: string; name: string; tagline: string; emoji: string; sampleSymbol: string; sampleSlug: string; sampleName: string }) => (
              <div
                key={c.slug}
                className="glass flex min-w-0 flex-col gap-2 rounded-xl p-4"
              >
                <Link
                  to="/categories/$slug"
                  params={{ slug: c.slug }}
                  className="flex items-center gap-3 hover:text-foreground"
                >
                  <span className="text-2xl text-gradient">{c.emoji}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold">{c.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {c.tagline}
                    </span>
                  </span>
                  <span className="text-muted-foreground">→</span>
                </Link>
                <Link
                  to="/symbol/$slug"
                  params={{ slug: c.sampleSlug }}
                  className="block truncate text-xs text-muted-foreground hover:text-accent"
                >
                  e.g. {c.sampleSymbol} {c.sampleName}
                </Link>
              </div>
            ))}
          </div>
        </section>

      </main>

      <SiteFooter />
    </div>
  );
}
